import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { postRequestAsync } from '../../helpers/apiHandler';
import CloseIcon from '../../layouts/SVG/CloseIcon';
import { showAlert } from '../../store/actions/alertAction';
import './_sandboxModal.scss';

const SandboxModal = ({ isOpen, onClose }) => {
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { access_token } = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleCheckboxChange = () => {
    setIsTermsAccepted(!isTermsAccepted);
  };

  const handleActivate = async () => {
    if (!isTermsAccepted) return;

    try {
      setIsSubmitting(true);

      const response = await postRequestAsync(
        '/v2/whatsapp/sandbox-activation',
        { hasAcceptedTerms: isTermsAccepted },
        access_token?.token
      );

      if (response && !response.error) {
        dispatch(showAlert('Sandbox activated successfully', 'success'));
        onClose();
        navigate('/dashboard/whatsapp/sandbox');
      } else {
        dispatch(showAlert(response.message || 'Failed to activate sandbox', 'danger'));
      }
    } catch (error) {
      console.error('Error activating sandbox:', error);
      dispatch(showAlert('An error occurred while activating sandbox', 'danger'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sandbox-modal-overlay" onClick={onClose}>
      <div className="sandbox-modal" onClick={e => e.stopPropagation()}>
        <div className="sandbox-modal-header">
          <h5 className="sandbox-modal-title">Activate Your Sandbox</h5>
          <CloseIcon handleModalClose={onClose} />
        </div>
        <div className="sandbox-modal-content">
          <div className="sandbox-content">
            <p>
              The elfoA2P Sandbox for WhatsApp provides access to a shared WhatsApp phone
              number. pre-registered WhatsApp developers. Using this, you can test
              the API by sending and receiving WhatsApp messages. To set up your Sandbox,
              you must first acknowledge and agree to the following:
            </p>

            <p>
              The WhatsApp applications, software, features, services, and APIs developed for
              businesses (<strong>"Third Party Services"</strong>) are independently provided by WhatsApp LLC
              or WhatsApp Ireland Limited (<strong>"Third Party Provider"</strong>). Your use of these Third Party
              Services is governed exclusively by (a) the <a href="https://www.whatsapp.com/legal/business-solution-terms" target="_blank" rel="noopener noreferrer" className="whatsapp-link">WhatsApp Business Solution Terms</a>, if
              you are not an independent software vendor, or (b) the <a href="https://www.facebook.com/legal/technology_provider_terms" target="_blank" rel="noopener noreferrer" className="whatsapp-link">Meta Business Messaging
                Technology Provider Terms</a>, if you are an independent software vendor ((a) or (b),
              as applicable, <strong>"Third Party Terms"</strong>). These terms (i) constitute an agreement
              between you and the Third Party Provider, which (ii) you acknowledge and accept.
              elfoA2P is not a party to the Third Party Terms and assumes no liability for the Third
              Party Services.
            </p>

            <div className="terms-checkbox">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={isTermsAccepted}
                  onChange={handleCheckboxChange}
                />
                <span className="checkmark"></span>
                <span className="checkbox-text">
                  I acknowledge and agree to the foregoing terms and conditions.
                </span>
              </label>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button className="wa-button-secondary mr-2" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </button>
              <button
                className={`wa-button-primary ${!isTermsAccepted || isSubmitting ? 'disabled' : ''}`}
                onClick={handleActivate}
                disabled={!isTermsAccepted || isSubmitting}
              >
                {isSubmitting ? 'Activating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SandboxModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default SandboxModal;